import React, { useState, useEffect } from 'react';
import { Users, Plus, Trash2, Edit2, Phone, Calendar, MapPin, Briefcase } from 'lucide-react';

const EmployeeConfig = () => {
  const [employees, setEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [newEmp, setNewEmp] = useState({ name: '', role: 'Filler', phone: '', date_of_birth: '', address: '', status: 'Active' });
  const [editingEmp, setEditingEmp] = useState(null);
  
  const isAdmin = localStorage.getItem('role') === 'admin';

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    setIsLoading(true);
    try {
      const res = await fetch('/api/employees', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      setEmployees(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setEmployees([]);
    }
    setIsLoading(false);
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    try {
      await fetch('/api/employees', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(newEmp)
      });
      setNewEmp({ name: '', role: 'Filler', phone: '', date_of_birth: '', address: '', status: 'Active' });
      fetchEmployees();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (!isAdmin) return alert('Only admins can remove employees.');
    if (!window.confirm('Are you sure you want to delete this Employee?')) return;
    try {
      await fetch(`/api/employees/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      fetchEmployees();
    } catch (err) {
      console.error(err);
    }
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    if (!isAdmin) return;
    try {
      await fetch(`/api/employees/${editingEmp.id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(editingEmp)
      });
      setEditingEmp(null);
      fetchEmployees();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-cyan-500 rounded-3xl p-8 text-white shadow-lg">
        <h1 className="text-3xl font-black mb-2 flex items-center gap-3">
          <Users size={32} />
          Staff & Employees
        </h1>
        <p className="text-blue-100 font-medium text-lg">Manage your pump fillers, cashiers, and staff details.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Add Employee Form */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden h-fit">
          <div className="p-5 border-b border-slate-100 bg-slate-50">
            <h2 className="font-bold text-slate-800 flex items-center gap-2">
              <Plus className="text-blue-500" size={20} />
              Register New Staff
            </h2>
          </div>
          <div className="p-6">
            <form onSubmit={handleAddEmployee} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Full Name *</label>
                <input required placeholder="e.g. John Doe" className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none" value={newEmp.name} onChange={e => setNewEmp({...newEmp, name: e.target.value})} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Role</label>
                  <select className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none" value={newEmp.role} onChange={e => setNewEmp({...newEmp, role: e.target.value})}>
                    <option value="Filler">Pump Filler</option>
                    <option value="Manager">Manager</option>
                    <option value="Sweeper">Sweeper</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Status</label>
                  <select className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none" value={newEmp.status} onChange={e => setNewEmp({...newEmp, status: e.target.value})}>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Phone Number</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-3.5 text-slate-400" size={18} />
                  <input type="tel" placeholder="10-digit number" className="w-full bg-white border border-slate-200 rounded-xl pl-10 pr-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none" value={newEmp.phone} onChange={e => setNewEmp({...newEmp, phone: e.target.value})} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Date of Birth</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-3.5 text-slate-400" size={18} />
                  <input type="date" className="w-full bg-white border border-slate-200 rounded-xl pl-10 pr-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none text-slate-700" value={newEmp.date_of_birth} onChange={e => setNewEmp({...newEmp, date_of_birth: e.target.value})} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Address / Notes</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 text-slate-400" size={18} />
                  <textarea rows="2" placeholder="Full address..." className="w-full bg-white border border-slate-200 rounded-xl pl-10 pr-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none resize-none" value={newEmp.address} onChange={e => setNewEmp({...newEmp, address: e.target.value})} />
                </div>
              </div>
              <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-xl transition-colors shadow-md shadow-blue-500/20 mt-4">
                Register Staff
              </button>
            </form>
          </div>
        </div>

        {/* Employee List */}
        <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-5 border-b border-slate-100 bg-slate-50 flex justify-between items-center">
            <h2 className="font-bold text-slate-800 flex items-center gap-2">
              <Briefcase className="text-blue-500" size={20} />
              Staff Roster
            </h2>
            <span className="bg-blue-100 text-blue-700 font-bold px-3 py-1 rounded-lg text-sm">{employees.length} Members</span>
          </div>
          
          <div className="overflow-x-auto">
            {isLoading ? (
              <div className="p-8 text-center text-slate-500 font-medium">Loading staff data...</div>
            ) : employees.length === 0 ? (
              <div className="p-12 text-center">
                <Users size={48} className="mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-bold text-slate-700 mb-2">No Staff Found</h3>
                <p className="text-slate-500">Register your first employee using the form on the left.</p>
              </div>
            ) : (
              <table className="w-full text-left text-sm">
                <thead className="bg-white text-slate-500 font-semibold border-b border-slate-100">
                  <tr>
                    <th className="p-4">Name</th>
                    <th className="p-4">Role</th>
                    <th className="p-4">Contact & Info</th>
                    <th className="p-4">Status</th>
                    {isAdmin && <th className="p-4 text-right">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {employees.map((emp) => (
                    <tr key={emp.id} className="hover:bg-slate-50 transition-colors">
                      <td className="p-4 font-bold text-slate-800">{emp.name}</td>
                      <td className="p-4 font-semibold text-slate-600 bg-slate-50/50">{emp.role}</td>
                      <td className="p-4 text-slate-600">
                        {emp.phone && <div className="text-xs font-mono mb-1">📞 {emp.phone}</div>}
                        {emp.date_of_birth && <div className="text-xs">🎂 {new Date(emp.date_of_birth).toLocaleDateString()}</div>}
                      </td>
                      <td className="p-4">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${emp.status === 'Active' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                          {emp.status}
                        </span>
                      </td>
                      {isAdmin && (
                        <td className="p-4 text-right">
                          <button onClick={() => setEditingEmp(emp)} className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors mr-2">
                            <Edit2 size={18} />
                          </button>
                          <button onClick={() => handleDelete(emp.id)} className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                            <Trash2 size={18} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Edit Modal (Admin Only) */}
      {isAdmin && editingEmp && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-6 w-full max-w-md shadow-2xl">
            <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2 mb-6">
              <Edit2 className="text-blue-500" size={24} />
              Edit Staff Details
            </h2>
            <form onSubmit={handleEditSave} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Full Name</label>
                <input required className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none" value={editingEmp.name} onChange={e => setEditingEmp({...editingEmp, name: e.target.value})} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Role</label>
                  <select className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none" value={editingEmp.role} onChange={e => setEditingEmp({...editingEmp, role: e.target.value})}>
                    <option value="Filler">Pump Filler</option>
                    <option value="Manager">Manager</option>
                    <option value="Sweeper">Sweeper</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Status</label>
                  <select className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none" value={editingEmp.status} onChange={e => setEditingEmp({...editingEmp, status: e.target.value})}>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Phone</label>
                <input type="tel" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none" value={editingEmp.phone || ''} onChange={e => setEditingEmp({...editingEmp, phone: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Date of Birth</label>
                <input type="date" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none text-slate-700" value={editingEmp.date_of_birth ? editingEmp.date_of_birth.split('T')[0] : ''} onChange={e => setEditingEmp({...editingEmp, date_of_birth: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Address / Notes</label>
                <textarea rows="2" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none resize-none" value={editingEmp.address || ''} onChange={e => setEditingEmp({...editingEmp, address: e.target.value})} />
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setEditingEmp(null)} className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold py-3 rounded-xl transition-colors">Cancel</button>
                <button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-xl transition-colors shadow-md shadow-blue-500/20">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeConfig;
